import * as THREE from 'three';
import { bus } from '@/core/EventBus';
import type { Player } from '@/gameplay/Player';
import type { Follower } from '@/gameplay/Follower';
import type { GameState, ReputationTrack } from '@/gameplay/GameState';

export type MissionStatus = 'running' | 'completed' | 'failed';
export type MissionBeatKind =
  | 'setup'
  | 'streetObjective'
  | 'humanBeat'
  | 'complication'
  | 'choiceOrConsequence'
  | 'reward';

export interface MissionPrerequisite {
  readonly flag: string;
  readonly value?: boolean;
}

export interface MissionReward {
  readonly money?: number;
  readonly reputation?: Partial<Record<ReputationTrack, number>>;
  readonly flags?: Readonly<Record<string, boolean>>;
}

export interface MissionBeat {
  readonly id: string;
  readonly kind: MissionBeatKind;
  readonly title: string;
  readonly objective: string;
  readonly setup?: string;
  readonly trigger?: string;
  readonly onEnter?: string;
  readonly onComplete?: string;
}

export interface MissionDefinition {
  readonly id: string;
  readonly title: string;
  readonly act: 0 | 1 | 2 | 3 | 4;
  readonly beats: readonly MissionBeat[];
  readonly prerequisites?: readonly MissionPrerequisite[];
  readonly rewards?: MissionReward;
}

export interface MissionProgressState {
  flag(id: string): boolean;
  setFlag(id: string, value?: boolean): void;
  earn(amount: number): void;
  changeReputation(track: ReputationTrack, delta: number): void;
}

export const STORY_MISSION_BEAT_KINDS = [
  'setup',
  'streetObjective',
  'humanBeat',
  'complication',
  'choiceOrConsequence',
  'reward',
] as const satisfies readonly MissionBeatKind[];

export function missionCompletedFlag(id: string): string {
  return `mission.${id}.completed`;
}

export function missionAvailable(definition: MissionDefinition, state: Pick<GameState, 'flag'>): boolean {
  return (definition.prerequisites ?? []).every((entry) => state.flag(entry.flag) === (entry.value ?? true));
}

export function applyMissionRewards(definition: MissionDefinition, state: MissionProgressState): void {
  const rewards = definition.rewards;
  if (!rewards) return;

  if (rewards.money !== undefined) state.earn(rewards.money);
  for (const [track, delta] of Object.entries(rewards.reputation ?? {}) as Array<[ReputationTrack, number]>) {
    state.changeReputation(track, delta);
  }
  for (const [flag, value] of Object.entries(rewards.flags ?? {})) {
    state.setFlag(flag, value);
  }
}

export interface SurvivorsMissionConfig {
  readonly definition?: MissionDefinition;
  readonly state?: MissionProgressState;
  readonly id?: string;
  readonly player: Player;
  readonly followers: readonly Follower[];
  readonly safehouse: THREE.Vector2;
}

export interface MissionSnapshot {
  readonly id: string;
  readonly status: MissionStatus;
  readonly objective: string;
  readonly rescued: number;
  readonly total: number;
  readonly down: number;
}

export class SurvivorsMission {
  readonly id: string;
  readonly definition: MissionDefinition | undefined;
  readonly #player: Player;
  readonly #followers: readonly Follower[];
  readonly #safehouse: THREE.Vector2;
  readonly #state: MissionProgressState | undefined;
  readonly #rescued = new Set<number>();
  readonly #down = new Set<number>();
  readonly #unsubscribers: Array<() => void> = [];

  #status: MissionStatus = 'running';
  #objective = '';

  constructor(config: SurvivorsMissionConfig) {
    this.definition = config.definition;
    this.id = config.id ?? config.definition?.id ?? 'na-01-overlevenden';
    this.#player = config.player;
    this.#followers = config.followers;
    this.#safehouse = config.safehouse.clone();
    this.#state = config.state;

    this.#unsubscribers.push(
      bus.on('soldier:rescued', ({ id }) => this.#markRescued(id)),
      bus.on('soldier:down', ({ id }) => this.#markDown(id)),
      bus.on('player:died', () => this.#fail('Luca is down')),
    );

    this.#setObjective(this.#pickupObjective());
  }

  get status(): MissionStatus {
    return this.#status;
  }

  update(): void {
    if (this.#status !== 'running') return;
    if (!this.#player.alive) {
      this.#fail('Luca is down');
      return;
    }

    for (const follower of this.#followers) {
      if (follower.rescued) this.#rescued.add(follower.id);
      else if (!follower.alive) this.#markDown(follower.id);
    }

    if (this.#rescued.size === this.#followers.length) {
      this.#complete();
      return;
    }

    const active = this.#followers.filter((follower) => follower.active).length;
    const next =
      active > 0
        ? `Escort the soldiers to the safehouse (${this.#rescued.size}/${this.#followers.length})`
        : this.#pickupObjective();
    this.#setObjective(next);
  }

  snapshot(): MissionSnapshot {
    return {
      id: this.id,
      status: this.#status,
      objective: this.#objective,
      rescued: this.#rescued.size,
      total: this.#followers.length,
      down: this.#down.size,
    };
  }

  dispose(): void {
    for (const unsubscribe of this.#unsubscribers) unsubscribe();
    this.#unsubscribers.length = 0;
  }

  #markRescued(id: number): void {
    if (this.#status !== 'running') return;
    this.#rescued.add(id);
    this.update();
  }

  #markDown(id: number): void {
    if (this.#status !== 'running') return;
    this.#down.add(id);
    this.#fail('A loyal soldier went down');
  }

  #complete(): void {
    if (this.#status !== 'running') return;
    this.#status = 'completed';
    this.#setObjective('All survivors reached the safehouse');
    if (this.definition && this.#state) {
      applyMissionRewards(this.definition, this.#state);
      this.#state.setFlag(missionCompletedFlag(this.definition.id), true);
    }
    bus.emit('mission:completed', { id: this.id });
  }

  #fail(reason: string): void {
    if (this.#status !== 'running') return;
    this.#status = 'failed';
    this.#setObjective(`Mission failed: ${reason}`);
    bus.emit('mission:failed', { id: this.id, reason });
  }

  #setObjective(text: string): void {
    if (text === this.#objective) return;
    this.#objective = text;
    bus.emit('mission:objectiveChanged', { text });
  }

  #pickupObjective(): string {
    const remaining = this.#followers.length - this.#rescued.size;
    const label = remaining === 1 ? 'soldier' : 'soldiers';
    const distance = Math.round(this.#player.position.distanceTo(this.#safehouse));
    return `Find ${remaining} loyal ${label} and reach the safehouse (${distance}m)`;
  }
}
